import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/common";
import { FormDialog } from "@/components/kids/kidsUi";
import { ACCEPT, checkFile, uploadFile } from "@/lib/upload13";

const ORANGE = "var(--glow-home)";

export function TaskDialog({ hubId, d, task, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    title: task?.title || "", category: task?.category || "Home", due: task?.due || d.today, every: task?.repeat?.every || "",
    unit: task?.repeat?.unit || "month", assignee_id: task?.assignee_id || "", appliance_id: task?.appliance_id || "", notes: task?.notes || "",
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, every: f.every ? Number(f.every) : null, unit: f.every ? f.unit : null, assignee_id: f.assignee_id || null, appliance_id: f.appliance_id || null };
      if (task) await api.put(`/organizations/${hubId}/home/tasks/${task.id}`, body); else await api.post(`/organizations/${hubId}/home/tasks`, body);
      toast.success(task ? "Task saved" : "Task added"); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <FormDialog open onClose={onClose} title={task ? "Edit task" : "Add a home care task"} testid="task-dialog">
      <div className="space-y-3">
        <Field label="Title"><input className="q-input w-full" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Replace HVAC filter" data-testid="task-title-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category"><select className="q-input w-full" value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="task-category-select">{d.categories.map((c) => <option key={c}>{c}</option>)}</select></Field>
          <Field label="Due"><input type="date" className="q-input w-full" value={f.due} onChange={(e) => set("due", e.target.value)} data-testid="task-due-input" /></Field>
        </div>
        <Field label="Repeats (leave empty for one time)">
          <div className="flex items-center gap-2">
            <span className="text-sm text-q-muted">Every</span>
            <input type="number" min="1" className="q-input w-20" value={f.every} onChange={(e) => set("every", e.target.value)} data-testid="task-every-input" />
            <select className="q-input flex-1" value={f.unit} onChange={(e) => set("unit", e.target.value)} data-testid="task-unit-select">{d.units.map((u) => <option key={u} value={u}>{u}s</option>)}</select>
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Assignee"><select className="q-input w-full" value={f.assignee_id} onChange={(e) => set("assignee_id", e.target.value)} data-testid="task-assignee-select"><option value="">Anyone</option>{d.members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></Field>
          <Field label="Linked item"><select className="q-input w-full" value={f.appliance_id} onChange={(e) => set("appliance_id", e.target.value)} data-testid="task-appliance-select"><option value="">None</option>{d.appliances.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></Field>
        </div>
        <Field label="Notes"><textarea className="q-input min-h-[60px] w-full" value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="task-notes-input" /></Field>
        <button className="q-btn q-btn-primary min-h-[44px] w-full" disabled={busy} onClick={save} data-testid="task-save-btn">{busy ? "Saving…" : "Save task"}</button>
      </div>
    </FormDialog>
  );
}

export function DoneDialog({ hubId, d, task, onClose, onSaved }) {
  const [f, setF] = useState({ date: d.today, note: "", cost: "", add: false });
  const [rcpt, setRcpt] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const receipt = rcpt ? await uploadFile(`/organizations/${hubId}/home`, rcpt) : null;
      const { data } = await api.post(`/organizations/${hubId}/home/tasks/${task.id}/done`, { date: f.date, note: f.note, cost: f.cost ? Number(f.cost) : null, add_to_spending: f.add, receipt_upload_id: receipt });
      toast.success(data.due ? `Done. Next due ${data.due}` : "Done"); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <FormDialog open onClose={onClose} title={`Mark “${task.title}” done`} testid="done-dialog">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Done on"><input type="date" className="q-input w-full" max={d.today} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="done-date-input" /></Field>
          <Field label="Cost (optional)"><input type="number" min="0" step="0.01" className="q-input w-full" value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} placeholder="0.00" data-testid="done-cost-input" /></Field>
        </div>
        <Field label="Note (optional)"><input className="q-input w-full" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="20x25 filter" data-testid="done-note-input" /></Field>
        {d.can_spend && (
          <label className="flex min-h-[44px] items-center gap-3 text-sm text-q-text">
            <input type="checkbox" className="h-5 w-5" checked={f.add} disabled={!f.cost} onChange={(e) => setF({ ...f, add: e.target.checked })} data-testid="done-add-spending" />
            Add to spending (Home category)
          </label>
        )}
        <label className="q-btn q-btn-secondary min-h-[44px] w-full cursor-pointer" data-testid="done-add-receipt">
          {rcpt ? `Receipt: ${rcpt.name}` : "Add receipt"}
          <input type="file" accept={ACCEPT} className="hidden" onChange={(e) => { const x = e.target.files[0]; e.target.value = ""; const bad = x && checkFile(x); if (bad) toast.error(bad); else setRcpt(x || null); }} data-testid="done-receipt-input" />
        </label>
        {rcpt && <p className="text-xs text-q-muted" data-testid="done-receipt-note">Filed in Documents under Warranties &amp; Manuals, linked to this task{task.appliance_name ? ` and ${task.appliance_name}` : ""}.</p>}
        <button className="q-btn q-btn-primary min-h-[44px] w-full" disabled={busy} onClick={save} data-testid="done-save-btn">{busy ? "Saving…" : "Mark done"}</button>
      </div>
    </FormDialog>
  );
}

export function PresetsDialog({ hubId, d, onClose, onSaved }) {
  const [pick, setPick] = useState([]);
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setPick((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/organizations/${hubId}/home/tasks/presets`, { ids: pick }); toast.success(`Added ${data.added.length} task${data.added.length === 1 ? "" : "s"}`); onSaved(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <FormDialog open onClose={onClose} title="Add from presets" description="Tap the ones you want. Each comes with a sensible repeat." testid="presets-dialog">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {d.presets.map((p) => {
          const on = pick.includes(p.id);
          return (
            <button key={p.id} onClick={() => toggle(p.id)} className="flex min-h-[52px] items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors duration-150"
              style={{ borderColor: on ? `rgb(${ORANGE})` : "var(--q-line, rgba(255,255,255,0.1))", background: on ? `rgba(${ORANGE},0.14)` : undefined }} aria-pressed={on} data-testid={`preset-${p.id}`}>
              <span className="text-xl">{p.icon}</span>
              <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold text-q-text">{p.title}</span><span className="text-xs text-q-muted">{p.category} · {p.repeat_label}</span></span>
            </button>
          );
        })}
      </div>
      <button className="q-btn q-btn-primary mt-4 min-h-[44px] w-full" disabled={busy || !pick.length} onClick={save} data-testid="presets-save-btn">{busy ? "Adding…" : `Add ${pick.length || ""} task${pick.length === 1 ? "" : "s"}`}</button>
    </FormDialog>
  );
}
